import React, { useState, useRef, useEffect } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';

// 28 Sep 2026 — /api/admin-ai now requires a signed-in admin.
async function adminJwt() {
  try { const { data } = await supabase.auth.getSession(); return (data && data.session && data.session.access_token) || ''; } catch (_e) { return ''; }
}

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const API = '/api/admin-ai';

const SUGGESTIONS = [
  'How many locations are ungeocoded in Cambodia?',
  'Show me the last 10 failed Sana queries',
  'What are the top 5 countries by location count?',
  'How many hospitals have er_24_7 = true?',
  'What is the Sana result rate this week?',
  'Show me locations in Phnom Penh missing coordinates',
  'How many support tickets are open?',
  'What types of locations does Thailand have?',
];

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

function renderToolResult(tr) {
  if (!tr || !tr.result) return null;
  const r = tr.result;
  if (r.error) return <div style={{ color: 'var(--color-text-danger)', fontSize: '.72rem', marginTop: 6 }}>Error: {r.error}</div>;
  if (tr.tool === 'write_data' && r.success) return <div style={{ color: '#28a745', fontSize: '.72rem', marginTop: 6 }}>✓ Update applied</div>;
  if (typeof r.count === 'number' && !r.rows) {
    return <div style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', marginTop: 6 }}>Count: <strong>{r.count.toLocaleString()}</strong></div>;
  }
  if (r.rows && r.rows.length > 0) {
    const cols = Object.keys(r.rows[0]).slice(0, 6);
    return (
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <table style={{ fontSize: '.68rem', borderCollapse: 'collapse', width: '100%', minWidth: 400 }}>
          <thead>
            <tr>{cols.map(c => <th key={c} style={{ padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid var(--color-border-secondary)', color: 'var(--color-text-secondary)', fontWeight: 500, whiteSpace: 'nowrap' }}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {r.rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: '.5px solid var(--color-border-tertiary)' }}>
                {cols.map(c => <td key={c} style={{ padding: '4px 8px', color: 'var(--color-text-primary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(row[c] ?? '—')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {r.rows.length === 20 && <div style={{ fontSize: '.65rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>Showing first 20 rows</div>}
      </div>
    );
  }
  return null;
}

function Message({ msg }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isUser = msg.role === 'user';
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 14 }}>
      <div style={{
        maxWidth: '82%',
        background: isUser ? '#314F5C' : 'var(--color-background-secondary)',
        color: isUser ? '#e8f4fb' : 'var(--color-text-primary)',
        borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        padding: '10px 14px',
        fontSize: '.78rem',
        lineHeight: 1.6,
        fontFamily: F,
      }}>
        {msg.content}
        {msg.toolResults && msg.toolResults.map((tr, i) => (
          <div key={i}>{renderToolResult(tr)}</div>
        ))}
      </div>
    </div>
  );
}

function AIAssistantBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I have direct access to the Sanoire database. Ask me anything — location counts, Sana metrics, coverage gaps, support tickets, or ask me to update data.' }
  ]);
  const [input, setInput]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [confirm, setConfirm]       = useState(null);
  const [partialText, setPartialText] = useState('');
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  async function send(text) {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: q };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setLoading(true);

    // Build API messages (exclude welcome message)
    const apiMsgs = newMsgs.slice(1).map(m => ({ role: m.role, content: m.content }));

    try {
      const res  = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (await adminJwt()) },
        body: JSON.stringify({ messages: apiMsgs }),
      });
      const data = await res.json();

      if (data.needsConfirmation) {
        setConfirm({ action: data.needsConfirmation, apiMsgs });
        setPartialText(data.partialText || '');
        if (data.partialText) {
          setMessages(prev => [...prev, { role: 'assistant', content: data.partialText, toolResults: data.toolResults }]);
        }
      } else if (data.text) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.text, toolResults: data.toolResults }]);
      } else if (data.error) {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + data.error }]);
      }
    } catch (_e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Network error. Please try again.' }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }

  async function confirmWrite() {
    if (!confirm) return;
    setConfirm(null);
    setLoading(true);
    try {
      const res  = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (await adminJwt()) },
        body: JSON.stringify({ messages: confirm.apiMsgs, confirmedWrite: confirm.action }),
      });
      const data = await res.json();
      if (data.text) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.text, toolResults: data.toolResults }]);
      }
    } catch (_e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Write failed. Please try again.' }]);
    }
    setLoading(false);
  }

  return (
    <div style={{ fontFamily: F, display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 80px)', padding: isMobile ? '0 0 0' : '0 0 0' }}>

      {/* Header */}
      <div style={{ padding: isMobile ? '12px 14px 8px' : '16px 24px 10px', borderBottom: '1px solid var(--color-border-tertiary)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>✦</div>
          <div>
            <div style={{ fontSize: '.9rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>Sanoire AI</div>
            <div style={{ fontSize: '.65rem', color: 'var(--color-text-secondary)' }}>Database access · Haiku 4.5 · ~$0.003/query</div>
          </div>
          <button onClick={() => setMessages([{ role: 'assistant', content: 'Hi! I have direct access to the Sanoire database. Ask me anything — location counts, Sana metrics, coverage gaps, support tickets, or ask me to update data.' }])}
            style={{ marginLeft: 'auto', fontSize: '.7rem', color: 'var(--color-text-secondary)', background: 'none', border: '1px solid var(--color-border-secondary)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontFamily: F }}>
            Clear
          </button>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px' : '20px 24px' }}>
        {messages.map((m, i) => <Message key={i} msg={m} />)}
        {loading && (
          <div style={{ display: 'flex', gap: 5, padding: '8px 14px' }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#6F8FA7', animation: `bounce 1s ease-in-out ${i * 0.15}s infinite` }} />
            ))}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Suggestions (only when 1 message = welcome) */}
      {messages.length === 1 && (
        <div style={{ padding: '0 24px 12px', display: 'flex', flexWrap: 'wrap', gap: 6, flexShrink: 0 }}>
          {SUGGESTIONS.slice(0, isMobile ? 4 : 8).map(s => (
            <button key={s} onClick={() => send(s)} style={{
              fontSize: '.68rem', padding: '5px 10px', borderRadius: 20,
              border: '1px solid var(--color-border-secondary)',
              background: 'var(--color-background-secondary)',
              color: 'var(--color-text-secondary)', cursor: 'pointer', fontFamily: F,
            }}>{s}</button>
          ))}
        </div>
      )}

      {/* Confirmation dialog */}
      {confirm && (
        <div style={{ margin: '0 24px 12px', borderRadius: 10, background: '#FAEEDA', border: '1px solid #BA7517', padding: '12px 16px', flexShrink: 0 }}>
          <div style={{ fontSize: '.75rem', fontWeight: 500, color: '#412402', marginBottom: 4 }}>Confirm write operation</div>
          <div style={{ fontSize: '.72rem', color: '#633806', marginBottom: 10 }}>{confirm.action.description}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={confirmWrite} style={{ padding: '5px 14px', borderRadius: 6, background: t.accent, color: '#AFE1F6', border: 'none', fontSize: '.72rem', cursor: 'pointer', fontFamily: F, fontWeight: 500 }}>Confirm</button>
            <button onClick={() => setConfirm(null)} style={{ padding: '5px 14px', borderRadius: 6, background: 'none', color: '#633806', border: '1px solid #BA7517', fontSize: '.72rem', cursor: 'pointer', fontFamily: F }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Input */}
      <div style={{ padding: isMobile ? '8px 14px 14px' : '8px 24px 20px', borderTop: '1px solid var(--color-border-tertiary)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about data, Sana metrics, geocoding, or request updates..."
            rows={2}
            style={{
              flex: 1, resize: 'none', borderRadius: 10, border: '1px solid var(--color-border-secondary)',
              padding: '10px 12px', fontSize: '.78rem', fontFamily: F,
              background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)',
              outline: 'none', lineHeight: 1.5,
            }}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || loading}
            style={{
              padding: '10px 16px', borderRadius: 10, border: 'none',
              background: input.trim() && !loading ? '#314F5C' : 'var(--color-background-tertiary)',
              color: input.trim() && !loading ? '#AFE1F6' : 'var(--color-text-secondary)',
              fontSize: '.78rem', fontWeight: 500, cursor: input.trim() && !loading ? 'pointer' : 'default',
              fontFamily: F, flexShrink: 0, transition: 'background .15s',
            }}
          >Send</button>
        </div>
        <div style={{ fontSize: '.62rem', color: 'var(--color-text-secondary)', marginTop: 5 }}>Enter to send · Shift+Enter for new line · Read-only by default, writes require confirmation</div>
      </div>

      <style>{`@keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }`}</style>
    </div>
  );
}

export default function AIAssistant(props) {
  if (props && props.embed) return <AIAssistantBody {...props} />;
  return <ConsoleShell title="AI Assistant"><AIAssistantBody {...props} /></ConsoleShell>;
}
